import trimEnd from "lodash/trimEnd";
import YAML from "yaml";

import {
  frequencyToPOSIXcronsyntax,
  PRODUCTION_PUBLISH_URL,
} from "../constants";
import { GhaOptions } from "../types";

/**
 * Functions for generating config files for different CI providers
 */

const getGitHubOnValue = (ghaOptions: GhaOptions) => {
  const onValue = new Map();
  if (ghaOptions.prEnabled) onValue.set("pull_request", {});
  if (ghaOptions.pushEnabled) {
    onValue.set("push", {
      branches: ghaOptions.pushBranches,
      paths: ghaOptions.pushPaths,
    });
  }
  if (ghaOptions.cronEnabled) {
    onValue.set("schedule", [
      { cron: `"${frequencyToPOSIXcronsyntax[ghaOptions.cronSchedule]}"` },
    ]);
  }
  return onValue;
};

const getGitHubArgs = (ghaOptions: GhaOptions) => {
  const args = new Map();

  if (ghaOptions.publishUrl !== PRODUCTION_PUBLISH_URL)
    args.set("SEMGREP_APP_URL", ghaOptions.publishUrl);

  return args.size === 0
    ? {}
    : { SEMGREP_APP_URL: args.get("SEMGREP_APP_URL") };
};

export const getGitHubYAML = (ghaOptions: GhaOptions) => {
  const onValue = getGitHubOnValue(ghaOptions);
  const maybeArgs = getGitHubArgs(ghaOptions);

  const workflowYaml = {
    name: "Semgrep",
    on: onValue,
    jobs: {
      semgrep: {
        name: "Scan",
        "runs-on": "ubuntu-20.04",
        env: {
          // eslint-disable-next-line no-template-curly-in-string
          SEMGREP_APP_TOKEN: "${{ secrets.SEMGREP_APP_TOKEN }}",
          ...maybeArgs,
        },

        container: {
          image: "returntocorp/semgrep",
        },

        if: "(github.actor != 'dependabot[bot]')",
        steps: [
          { uses: "actions/checkout@v3" },
          // {
          //   ...maybeArgs,
          // },
          { run: "semgrep ci" },
        ],
      },
    },
  };

  return YAML.stringify(workflowYaml).replace("'\"", "'").replace("\"'", "'");
};

export const getGitLabYAML = (
  scanMerge: boolean,
  scanPush: boolean,
  sendResults: boolean,
  deploymentId?: string
) =>
  trimEnd(`semgrep:
  image: returntocorp/semgrep
  script: semgrep ci${
    sendResults ? " --gitlab-sast > gl-sast-report.json || true" : ""
  }
  rules:
  - changes:
      - .gitlab-ci.yml
  - if: $CI_PIPELINE_SOURCE == "web"  # allow triggering a scan manually from the gitlab UI
  ${scanMerge ? "- if: $CI_MERGE_REQUEST_IID\n  " : ""}${
    scanPush ? "- if: $CI_COMMIT_BRANCH == $CI_DEFAULT_BRANCH" : ""
  }${!(scanMerge || scanPush) ? "# TODO: add your own" : ""}
  variables:
    ${
      deploymentId === undefined
        ? `SEMGREP_RULES: >- # more at semgrep.dev/r
      p/security-audit
      p/secrets`
        : window.location.hostname !== "semgrep.dev"
        ? `SEMGREP_APP_URL: ${window.origin}`
        : ""
    }
    SEMGREP_APP_TOKEN: $SEMGREP_APP_TOKEN
  ${
    sendResults
      ? `artifacts:
    reports:
      sast: gl-sast-report.json`
      : ""
  }
  # Setup inline MR comments:
  # https://semgrep.dev/docs/semgrep-app/notifications/#enabling-gitlab-merge-request-comments
`);

export interface CIYamlOptions {
  scanChangedOnly: boolean;
  defaultBranch: string;
  useDefaultBranch: boolean;
  diffBranch: string;
}

const DEFAULT_CI_YAML_OPTIONS: CIYamlOptions = {
  scanChangedOnly: false,
  defaultBranch: "",
  useDefaultBranch: false,
  diffBranch: "",
};

export const getJenkinsCIYAML = (
  config: CIYamlOptions = DEFAULT_CI_YAML_OPTIONS
) =>
  trimEnd(`
  pipeline {
    agent any
  environment {
      ${config.scanChangedOnly ? " " : "//"} SEMGREP_BASELINE_REF = "${
    config.diffBranch
  }"

        SEMGREP_APP_TOKEN = credentials('SEMGREP_APP_TOKEN')
        SEMGREP_PR_ID = "\${env.CHANGE_ID}"

      //  SEMGREP_TIMEOUT = "300"
    }
    stages {
      stage('Semgrep-Scan') {${
        config.useDefaultBranch
          ? `
          when {
            branch "${config.defaultBranch}"
          }`
          : ""
      }
          steps {
            sh 'pip3 install semgrep'
            sh 'semgrep ci${
              config.scanChangedOnly
                ? " --baseline-commit $SEMGREP_BASELINE_REF'"
                : "'"
            }
          }
      }
    }
  }
`);

export const getBitbucketCIYAML = (
  config: CIYamlOptions = DEFAULT_CI_YAML_OPTIONS
) =>
  trimEnd(`
  image: atlassian/default-image:latest

  pipelines:
    ${
      config.useDefaultBranch
        ? `branches:
      ${config.defaultBranch}:  `
        : `default:
      - parallel:`
    }
        - step:
            name: 'Run Semgrep scan with current branch'
            deployment: test
            image: returntocorp/semgrep
            script:
              # Set SEMGREP Variables
              ${
                config.scanChangedOnly ? "" : "# "
              }- export SEMGREP_BASELINE_REF="${config.diffBranch}"
              - export SEMGREP_APP_TOKEN=$SEMGREP_APP_TOKEN
              ${
                config.scanChangedOnly
                  ? `- git fetch --unshallow
              - git config remote.origin.fetch "+refs/heads/*:refs/remotes/origin/*"
              - git fetch origin
              - git checkout --track origin/${config.diffBranch}
              `
                  : ""
              }
              - semgrep ci${
                config.scanChangedOnly
                  ? " --baseline-commit $SEMGREP_BASELINE_REF"
                  : ""
              }
`);

export const getCircleCIYAML = (
  config: CIYamlOptions = DEFAULT_CI_YAML_OPTIONS
) =>
  trimEnd(`version: 2.1

jobs:
  semgrep-scan:
    parameters: ${
      config.scanChangedOnly
        ? `
      diff_branch:
        type: string
        default: ${config.diffBranch}`
        : ""
    }

    environment:
      # Scan changed files in PRs, block on new issues only (existing issues ignored)
      ${
        config.scanChangedOnly ? "" : "# "
      }SEMGREP_BASELINE_REF: << parameters.diff_branch >>
      SEMGREP_REPO_URL: << pipeline.project.git_url >>
      SEMGREP_BRANCH: << pipeline.git.branch >>

      # Change job timeout (default is 1800 seconds; set to 0 to disable)
      # SEMGREP_TIMEOUT: 300

    docker:
      - image: returntocorp/semgrep
    steps:
      - checkout
      - run:
          name: "Semgrep scan"
          command: semgrep ci${
            config.scanChangedOnly
              ? " --baseline-commit $SEMGREP_BASELINE_REF"
              : ""
          }
workflows:
  Semgrep:
    jobs:
      - semgrep-scan${
        config.useDefaultBranch
          ? `:
          filters:
            branches:
              only: ${config.defaultBranch}`
          : ""
      }

`);

export const getBuildkiteCIYAML = (
  config: CIYamlOptions = DEFAULT_CI_YAML_OPTIONS
) =>
  trimEnd(`- label: ":semgrep: Semgrep"
  commands:
    - export SEMGREP_BASELINE_REF="${config.diffBranch}"
    - semgrep ci ${
      config.scanChangedOnly ? "--baseline-commit $SEMGREP_BASELINE_REF" : ""
    }
  ${config.useDefaultBranch ? `branches: "${config.defaultBranch}"` : ""}
  plugins:
    - docker#v3.7.0:
        image: returntocorp/semgrep
        environment:
          # Scan with rules set in Semgrep App's rule board
          # Make a token at semgrep.dev/orgs/-/settings/tokens
          - "SEMGREP_APP_TOKEN"
`);

export const getAzureCIYAML = (
  config: CIYamlOptions = DEFAULT_CI_YAML_OPTIONS
) =>
  trimEnd(`${
    config.useDefaultBranch
      ? `trigger:
- ${config.defaultBranch}`
      : `trigger:
- master`
  }

pool:
  vmImage: ubuntu-latest
variables:
- group: Semgrep app token group

steps:
- script: |
    python -m pip install --upgrade pip
    pip install semgrep
    semgrep ci
  env:
    SEMGREP_PR_ID: $(System.PullRequest.PullRequestNumber)
    ${
      config.scanChangedOnly
        ? "SEMGREP_BASELINE_REF: $(System.PullRequest.TargetBranch)"
        : ""
    }
`);
